import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { toast } from 'sonner';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { Shield, ChevronsUpDown, Check } from 'lucide-react';
import { CreateCustomCourse } from './CreateCustomCourse';

export function AddQualification({
  isOpen,
  onClose,
  projects,
  onSuccess,
  defaultProjectId,
  teamMemberCount
}) {
  const { userProfile, company } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'medium',
    project_ids: defaultProjectId ? [defaultProjectId] : [],
    calculated_required: null,
    renewal_period: '',
    category: '',
    regulatory_body: '',
    regulation_reference: '',
    legal_requirement: false,
    notes: '',
    selectedCourseType: null,
    selectedCourseId: null
  });

  // Display label for AsyncCombobox
  const [courseDisplayLabel, setCourseDisplayLabel] = useState('');

  // Track whether user wants number or percentage
  const [requirementType, setRequirementType] = useState('number');

  // Create custom course modal state
  const [isCreateCourseOpen, setIsCreateCourseOpen] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [previousCourseName, setPreviousCourseName] = useState('');

  // Set default project ID when dialog opens
  useEffect(() => {
    if (isOpen && defaultProjectId) {
      setFormData(prev => ({
        ...prev,
        project_ids: [defaultProjectId]
      }));
    }
  }, [isOpen, defaultProjectId]);

  // Handle main dialog close - reset form before closing
  const handleDialogClose = () => {
    // Reset all form data
    setFormData({
      name: '',
      description: '',
      priority: 'medium',
      project_ids: [],
      calculated_required: null,
      renewal_period: '',
      category: '',
      regulatory_body: '',
      regulation_reference: '',
      legal_requirement: false,
      notes: '',
      selectedCourseType: null,
      selectedCourseId: null
    });
    setRequirementType('number');
    setCourseDisplayLabel('');

    // Call parent's onClose
    onClose();
  };

  // Handle create new custom course
  const handleCreateNewCourse = searchTerm => {
    setPreviousCourseName(formData.name);
    setNewCourseTitle(searchTerm);
    setIsCreateCourseOpen(true);
  };

  // Handle Create Custom Course modal cancellation (close without creating)
  const handleCreateCourseCancel = () => {
    setIsCreateCourseOpen(false);
    setFormData(prev => ({ ...prev, name: previousCourseName }));
  };

  // Handle Create Custom Course modal close (generic)
  const handleCreateCourseClose = () => {
    setIsCreateCourseOpen(false);
  };

  // Handle successful custom course creation
  const handleCustomCourseCreated = createdCourse => {
    setFormData(prev => ({
      ...prev,
      name: createdCourse.title,
      description: createdCourse.description || '',
      selectedCourseType: 'custom',
      selectedCourseId: createdCourse.id
    }));
    setCourseDisplayLabel(createdCourse.title);
    toast.success(`"${createdCourse.title}" has been selected`);
  };

  // Search handler for qualifications
  const handleQualificationSearch = async searchTerm => {
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }

    try {
      // Parallel API calls
      const [apiResults, dbResults] = await Promise.all([
        // Call 1: BOHSA courses API (searches the courses table with fuzzy matching)
        fetch(
          `https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses?search=${encodeURIComponent(searchTerm)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX'
            }
          }
        )
          .then(res => res.json())
          .then(response => {
            // API returns { data: [...], message: "...", type: "..." }
            const courses = response?.data || [];

            // Transform API results to { value, label, group, data } format
            if (Array.isArray(courses)) {
              return courses.map(course => ({
                value: course.name,
                label: course.name,
                group: 'Verified Courses',
                data: { ...course, title: course.name }
              }));
            }
            return [];
          })
          .catch(err => {
            console.error('API search error:', err);
            return [];
          }),

        // Call 2: Supabase custom_courses table
        supabase
          .from('custom_courses')
          .select('*')
          .ilike('title', `%${searchTerm}%`)
          .or(`company_id.eq.${company?.id},company_id.is.null`)
          .limit(10)
          .then(({ data, error }) => {
            if (error) {
              console.error('Database search error:', error);
              return [];
            }

            // Transform DB results to { value, label, group, data } format
            return (data || []).map(course => ({
              value: course.title || course.name,
              label: course.title || course.name,
              group: 'Custom Courses',
              data: course
            }));
          })
      ]);

      // Combine and deduplicate results
      const combinedResults = [...apiResults, ...dbResults];

      // Remove duplicates based on value (title)
      const uniqueResults = combinedResults.reduce((acc, current) => {
        const exists = acc.find(item => item.value === current.value);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      return uniqueResults;
    } catch (error) {
      console.error('Search error:', error);
      return [];
    }
  };

  // Handle dialog open/close state changes
  const handleOpenChange = open => {
    if (open) {
      // Reset form data when opening
      setFormData({
        name: '',
        description: '',
        priority: 'medium',
        project_id: '',
        calculated_required: null,
        renewal_period: '',
        category: '',
        regulatory_body: '',
        regulation_reference: '',
        legal_requirement: false,
        notes: '',
        selectedCourseType: null,
        selectedCourseId: null
      });
      setRequirementType('number');
      setCourseDisplayLabel('');
    } else {
      // Use the close handler when closing
      handleDialogClose();
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name || formData.project_ids.length === 0) {
        toast.error('Please fill in required fields');
        return;
      }

      // Store integer value in calculated_required and type in minimum_personnel
      const calculatedValue = formData.calculated_required;
      const minimumPersonnelValue =
        requirementType === 'percentage'
          ? `${formData.calculated_required}%`
          : String(formData.calculated_required || '');

      // Insert one record per selected space
      for (const pid of formData.project_ids) {
        // Get the maximum display_order for this project
        const { data: maxOrderData } = await supabase
          .from('compliance_requirements')
          .select('display_order')
          .eq('project_id', pid)
          .eq('show_on_matrix', true)
          .order('display_order', { ascending: false, nullsLast: true })
          .limit(1);

        const nextDisplayOrder = maxOrderData && maxOrderData.length > 0 && maxOrderData[0].display_order !== null
          ? maxOrderData[0].display_order + 1
          : 0;

        const { error } = await supabase.from('compliance_requirements').insert({
          name: formData.name,
          description: formData.description || null,
          priority: formData.priority,
          project_id: pid,
          calculated_required: calculatedValue,
          minimum_personnel: minimumPersonnelValue,
          renewal_period: formData.renewal_period || null,
          category: formData.category || null,
          regulatory_body: formData.regulatory_body || null,
          regulation_reference: formData.regulation_reference || null,
          legal_requirement: formData.legal_requirement,
          notes: formData.notes || null,
          created_by: userProfile?.id,
          status: 'active',
          created_date: new Date().toISOString(),
          show_on_matrix: true,
          display_order: nextDisplayOrder,
          requirement_type: 'course',
          approval_status: 'approved',
          course_link_id: formData.selectedCourseId || null,
          course_link_type: formData.selectedCourseType || null
        });

        if (error) throw error;
      }

      const spaceCount = formData.project_ids.length;
      toast.success(spaceCount > 1
        ? `Qualification added to ${spaceCount} spaces`
        : 'Qualification added successfully'
      );
      handleDialogClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error adding qualification:', error);
      if (error.code === '23505') {
        toast.error('A qualification with this name already exists in the selected space');
      } else {
        toast.error('Failed to add qualification');
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Add New Qualification</DialogTitle>
          <DialogDescription>
            Create a new training qualification or requirement.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='name'>Name *</Label>
            <AsyncCombobox
              value={formData.name}
              displayLabel={courseDisplayLabel}
              onValueChange={value => {
                setFormData({ ...formData, name: value });
                // Clear display label when user types
                if (!value) {
                  setCourseDisplayLabel('');
                }
              }}
              onSelectionChange={courseData => {
                // Update course name and display label
                const courseName = courseData?.title || courseData?.name;
                if (courseName) {
                  setCourseDisplayLabel(courseName);
                }

                // `courseData` is the `data` object of the selected item.
                // We determine the type by checking for a property that only exists on custom courses.
                const type = courseData.hasOwnProperty('company_id')
                  ? 'custom'
                  : 'verified';
                const id = courseData?.id;

                setFormData(prev => ({
                  ...prev,
                  name: courseName,
                  description: courseData?.description || '',
                  selectedCourseType: type,
                  selectedCourseId: id
                }));
              }}
              onSearch={handleQualificationSearch}
              onCreateNew={handleCreateNewCourse}
              createNewLabel='Create custom course'
              createNewGroup='Custom Courses'
              groupConfig={{
                'Verified Courses': {
                  icon: Shield,
                  color: '#3b82f6' // blue-500
                }
              }}
              placeholder='Search for a qualification...'
              emptyText='No qualifications found. Try a different search term.'
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='description'>Description</Label>
            <Textarea
              id='description'
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder='Brief description of the qualification...'
              rows={3}
            />
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='project'>{defaultProjectId && projects.length === 1 ? 'Space' : 'Spaces'} *</Label>
              {defaultProjectId && projects.length === 1 ? (
                // Show as plain text when in a single space context
                <div className='text-sm text-gray-900 font-medium pt-2'>
                  {projects[0]?.name || 'Selected Space'}
                </div>
              ) : (
                // Searchable multi-select for global/stack context
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant='outline' role='combobox' className='w-full justify-between font-normal'>
                      {formData.project_ids.length === 0
                        ? 'Select spaces...'
                        : `${formData.project_ids.length} space${formData.project_ids.length !== 1 ? 's' : ''} selected`}
                      <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-full p-0' align='start'>
                    <Command>
                      <CommandInput placeholder='Search spaces...' />
                      <CommandList>
                        <CommandEmpty>No spaces found.</CommandEmpty>
                        <CommandGroup className='max-h-64 overflow-auto'>
                          {projects.map(project => {
                            const isSelected = formData.project_ids.includes(project.id);
                            return (
                              <CommandItem
                                key={project.id}
                                value={project.name}
                                onSelect={() => {
                                  setFormData({
                                    ...formData,
                                    project_ids: isSelected
                                      ? formData.project_ids.filter(id => id !== project.id)
                                      : [...formData.project_ids, project.id]
                                  });
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                                {project.name}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='priority'>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={value =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='high'>High</SelectItem>
                  <SelectItem value='medium'>Medium</SelectItem>
                  <SelectItem value='low'>Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='category'>Category</Label>
              <Select
                value={formData.category}
                onValueChange={value =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select category' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='Equipment'>Equipment</SelectItem>
                  <SelectItem value='Safety'>Safety</SelectItem>
                  <SelectItem value='Compliance'>Compliance</SelectItem>
                  <SelectItem value='Training'>Training</SelectItem>
                  <SelectItem value='Certification'>Certification</SelectItem>
                  <SelectItem value='Environmental'>Environmental</SelectItem>
                  <SelectItem value='Quality'>Quality</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-3'>
              <Label className='text-sm font-medium text-gray-900'>
                Required Personnel
              </Label>

              {/* Premium Toggle Selector */}
              <div className='inline-flex items-center bg-gray-100 rounded-lg p-1 gap-1'>
                <button
                  type='button'
                  onClick={() => setRequirementType('number')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    requirementType === 'number'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Specific Number
                </button>
                <button
                  type='button'
                  onClick={() => setRequirementType('percentage')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    requirementType === 'percentage'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Percentage
                </button>
              </div>

              {/* Input field */}
              <div className='relative'>
                <Input
                  id='required'
                  type='number'
                  min='0'
                  max={requirementType === 'percentage' ? 100 : undefined}
                  value={formData.calculated_required || ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      calculated_required: e.target.value
                        ? parseInt(e.target.value)
                        : null
                    })
                  }
                  placeholder={
                    requirementType === 'percentage' ? 'e.g., 100' : 'e.g., 20'
                  }
                  className={requirementType === 'percentage' ? 'pr-10' : ''}
                />
                {requirementType === 'percentage' && (
                  <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded bg-gray-100 pointer-events-none'>
                    <span className='text-sm font-medium text-gray-600'>%</span>
                  </div>
                )}
              </div>
              {requirementType === 'percentage' && teamMemberCount > 0 && formData.calculated_required > 0 && (
                <p className='text-xs text-gray-500 mt-1'>
                  = <span className='font-medium text-gray-700'>{Math.ceil((formData.calculated_required / 100) * teamMemberCount)}</span> of {teamMemberCount} team members
                </p>
              )}
            </div>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='validity'>Renewal Period</Label>
            <Select
              value={formData.renewal_period || undefined}
              onValueChange={value =>
                setFormData({ ...formData, renewal_period: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Select renewal period' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>No Renewal Required</SelectItem>
                <SelectItem value='6_months'>Every 6 Months</SelectItem>
                <SelectItem value='1_year'>Every Year</SelectItem>
                <SelectItem value='2_years'>Every 2 Years</SelectItem>
                <SelectItem value='3_years'>Every 3 Years</SelectItem>
                <SelectItem value='5_years'>Every 5 Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Add Qualification</Button>
        </DialogFooter>
      </DialogContent>

      {/* Create Custom Course Modal */}
      <CreateCustomCourse
        isOpen={isCreateCourseOpen}
        onClose={handleCreateCourseClose}
        onCancel={handleCreateCourseCancel}
        initialTitle={newCourseTitle}
        onSuccess={handleCustomCourseCreated}
      />
    </Dialog>
  );
}
